import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import JSON5 from 'json5';
import rawTranslations from './translations.json5?raw';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import { numberFormatter } from './number-formatter';

import { DEFAULT_LOCALE, RTL_LOCALES, SUPPORTED_LOCALES, type AppLocale } from './locales';

export { DEFAULT_LOCALE, RTL_LOCALES, SUPPORTED_LOCALES, type AppLocale };
export const LOCALE_STORAGE_KEY = 'bakedshift-locale';

type LeafValue = Partial<Record<AppLocale, string>>;
type TranslationNode = { [key: string]: TranslationNode | LeafValue };

function isLeaf(node: unknown): node is LeafValue {
  if (typeof node !== 'object' || node === null) return false;
  const keys = Object.keys(node);
  return keys.length > 0 && keys.every((k) => SUPPORTED_LOCALES.includes(k as AppLocale));
}

type Resources = Record<AppLocale, Record<string, unknown>>;

const UNSAFE_KEYS: ReadonlySet<string> = new Set(['__proto__', 'prototype', 'constructor']);

/** The CLDR plural forms not every locale has (en has one/other; fr adds many; ar all six). */
const PLURAL_FORM = /_(zero|two|few|many)$/;

function createBag(): Record<string, unknown> {
  return Object.create(null) as Record<string, unknown>;
}

function isSafeKey(key: string): boolean {
  return !UNSAFE_KEYS.has(key);
}

function pivot(tree: TranslationNode): Resources {
  const resources: Resources = {
    en: createBag(),
    fr: createBag(),
    ar: createBag(),
  };

  function walk(node: TranslationNode, path: string[]): void {
    for (const [key, value] of Object.entries(node)) {
      if (!isSafeKey(key)) continue;
      const nextPath = [...path, key];
      if (isLeaf(value)) {
        const fallback = value.en ?? Object.values(value)[0] ?? '';
        // A plural FORM written for some locales only (`_zero: { ar }`,
        // `_many: { fr, ar }`) stays missing in the others: filled, it leaked
        // the Arabic `_zero` sentence into English and French at count 0,
        // because i18next reads `_zero` for 0 in every language (uiv2 7e).
        const localeOnlyForm = PLURAL_FORM.test(key);
        for (const locale of SUPPORTED_LOCALES) {
          const translated = value[locale] ?? (localeOnlyForm ? undefined : fallback);
          if (translated !== undefined) setDeep(resources[locale], nextPath, translated);
        }
      } else if (typeof value === 'object' && value !== null) {
        walk(value as TranslationNode, nextPath);
      }
    }
  }

  function setDeep(target: Record<string, unknown>, path: string[], leafValue: string): void {
    let cursor: Record<string, unknown> = target;
    for (let i = 0; i < path.length - 1; i += 1) {
      const segment = path[i];
      if (!isSafeKey(segment)) return;
      const existing = cursor[segment];
      if (typeof existing !== 'object' || existing === null || Array.isArray(existing)) {
        const fresh = createBag();
        Object.defineProperty(cursor, segment, {
          value: fresh,
          writable: true,
          enumerable: true,
          configurable: true,
        });
        cursor = fresh;
      } else {
        cursor = existing as Record<string, unknown>;
      }
    }
    const leafKey = path[path.length - 1];
    if (!isSafeKey(leafKey)) return;
    Object.defineProperty(cursor, leafKey, {
      value: leafValue,
      writable: true,
      enumerable: true,
      configurable: true,
    });
  }

  walk(tree, []);
  return resources;
}

const parsed = JSON5.parse(rawTranslations) as TranslationNode;
const resources = pivot(parsed);

function resolveInitialLocale(): AppLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored as AppLocale)) {
    return stored as AppLocale;
  }
  return DEFAULT_LOCALE;
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(numberFormatter)
  .init({
    resources: {
      en: { translation: resources.en },
      fr: { translation: resources.fr },
      ar: { translation: resources.ar },
    },
    lng: resolveInitialLocale(),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    // `alwaysFormat`: every interpolated number is grouped in the reader's
    // language by `numberFormatter`, with no opt-in per placeholder (uiv2 7c).
    interpolation: { escapeValue: false, alwaysFormat: true },
    returnNull: false,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

/**
 * dayjs speaks the reader's language (uiv2 7c): antd's calendars and every
 * `fromNow()` ("il y a 3 jours") read dayjs's GLOBAL locale, which nothing set
 * — month names were English in French and Arabic. Week arithmetic does not
 * read it: `lib/week.ts`.
 */
function syncDayjsLocale(lng: string | undefined): void {
  const base = (lng ?? DEFAULT_LOCALE).split('-')[0] as AppLocale;
  dayjs.locale(SUPPORTED_LOCALES.includes(base) ? base : DEFAULT_LOCALE);
}
i18n.on('languageChanged', syncDayjsLocale);
syncDayjsLocale(i18n.language);

export function isRtl(locale: AppLocale): boolean {
  return RTL_LOCALES.has(locale);
}

export default i18n;
